import {
  ExternalLink,
  ShoppingCart,
  Sparkles,
  Flame,
  BookOpen,
  Lamp,
  Circle,
  Backpack,
  Shirt,
  Landmark,
} from 'lucide-react';

const AMAZON_TAG = 'jyotirlinga12-21';

// Each product uses a themed lucide-react icon rendered on a saffron gradient tile.
// This upgrade from single emoji glyphs gives crisp, on-brand product category art
// that always loads. When you have Amazon PA-API credentials, swap the `Icon`
// with a real product `image` URL fetched via the API.
const products = [
  {
    Icon: Sparkles,
    tint: 'from-saffron-400 to-orange-500',
    name: 'Rudraksha Mala',
    description: 'Original 108-bead prayer mala for daily japa',
    keyword: 'rudraksha mala original 108 beads',
  },
  {
    Icon: Flame,
    tint: 'from-orange-400 to-red-500',
    name: 'Puja Thali Set',
    description: 'Complete brass puja thali with all accessories',
    keyword: 'puja thali brass set',
  },
  {
    Icon: BookOpen,
    tint: 'from-yellow-500 to-orange-600',
    name: 'Shiva Purana',
    description: 'Sacred scripture of Lord Shiva in English',
    keyword: 'shiva purana english book',
  },
  {
    Icon: Lamp,
    tint: 'from-amber-500 to-orange-500',
    name: 'Brass Diya & Lamp',
    description: 'Traditional oil lamp for temple worship',
    keyword: 'brass diya oil lamp puja',
  },
  {
    Icon: Circle,
    tint: 'from-saffron-500 to-yellow-600',
    name: 'Meditation Cushion',
    description: 'Zafu cushion for meditation & chanting',
    keyword: 'meditation cushion zafu',
  },
  {
    Icon: Backpack,
    tint: 'from-orange-500 to-amber-600',
    name: 'Pilgrimage Backpack',
    description: 'Durable 40L travel backpack for temple visits',
    keyword: 'travel backpack 40l',
  },
  {
    Icon: Shirt,
    tint: 'from-red-400 to-orange-500',
    name: 'Ethnic Wear',
    description: 'Traditional kurta, saree & salwar for pilgrims',
    keyword: 'ethnic wear traditional indian kurta saree',
  },
  {
    Icon: Landmark,
    tint: 'from-orange-600 to-saffron-500',
    name: 'Shiv Idols',
    description: 'Brass, marble & panchdhatu Shiva idols for puja',
    keyword: 'shiva idol brass murti puja statue',
  },
];

const buildAmazonUrl = (keyword) =>
  `https://www.amazon.in/s?k=${encodeURIComponent(keyword)}&tag=${AMAZON_TAG}`;

export const AmazonEssentials = ({ temple }) => {
  const heading = temple
    ? `Essentials for Your ${temple.name} Yatra`
    : 'Pilgrimage Essentials for Your Jyotirlinga Yatra';
  const subheading = temple
    ? `Curated puja items, spiritual books, and travel essentials for pilgrims visiting ${temple.name} and other sacred Jyotirlinga temples. Delivered by Amazon India.`
    : 'Curated puja items, spiritual books, and travel essentials for pilgrims visiting the 12 sacred Jyotirlinga temples. Delivered by Amazon India.';

  return (
    <section className="py-16 md:py-24 bg-background" data-testid="amazon-essentials-section">
      <div className="container mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-1 bg-orange-100 text-orange-700 font-outfit text-xs uppercase tracking-wider mb-4">
            Pilgrimage Essentials
          </div>
          <h2 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-temple-slate mb-4">
            {heading}
          </h2>
          <p className="font-outfit text-base text-gray-600 max-w-2xl mx-auto">
            {subheading}
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          {products.map((product) => {
            const Icon = product.Icon;
            return (
              <a
                key={product.name}
                href={buildAmazonUrl(product.keyword)}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="group bg-white border border-orange-100/50 hover:border-orange-300 hover:-translate-y-1 hover:shadow-lg transition-all flex flex-col overflow-hidden"
                data-testid={`amazon-product-${product.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div
                  className={`aspect-square bg-gradient-to-br ${product.tint} flex items-center justify-center relative overflow-hidden`}
                >
                  {/* Ambient decorative rings */}
                  <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/10"></div>
                  <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-white/10"></div>
                  <Icon className="w-16 h-16 text-white relative z-10 group-hover:scale-110 transition-transform duration-300" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-cormorant text-xl font-bold text-temple-slate mb-2">
                    {product.name}
                  </h3>
                  <p className="font-outfit text-xs text-gray-600 mb-4 flex-1">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between text-orange-600 group-hover:text-orange-700">
                    <span className="font-outfit text-xs font-medium flex items-center gap-1">
                      <ShoppingCart className="w-3.5 h-3.5" />
                      Shop on Amazon
                    </span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        <p className="text-center mt-10 font-outfit text-xs text-gray-500 max-w-xl mx-auto">
          As an Amazon Associate, we earn from qualifying purchases — at no extra cost to you. Prices, availability, and product details are managed by Amazon.in.
        </p>
      </div>
    </section>
  );
};
